/**
 * In-app reset-password screen (FR-2.11) — enter the emailed reset code + a new
 * password (validated against the pool policy). The hook confirms the reset and returns
 * to sign-in for the user to sign in with the new password.
 */

import { useState } from 'react';
import { validateCode, validatePassword } from './validation';

export interface ResetPasswordFormProps {
  busy: boolean;
  error: string | null;
  notice: string | null;
  onSubmit: (code: string, newPassword: string) => void;
}

export function ResetPasswordForm({
  busy,
  error,
  notice,
  onSubmit,
}: ResetPasswordFormProps): React.JSX.Element {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const invalid = validateCode(code) ?? validatePassword(password);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    setLocalError(null);
    onSubmit(code.trim(), password);
  };

  const shownError = localError ?? error;

  return (
    <form className="auth-form" data-testid="auth-reset" onSubmit={submit} noValidate>
      <h2 className="auth-form__title">Choose a new password</h2>
      {notice && (
        <div className="auth-form__notice" data-testid="auth-notice">
          {notice}
        </div>
      )}
      <input
        aria-label="Reset code"
        data-testid="auth-code"
        placeholder="Reset code"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoFocus
      />
      <input
        type="password"
        aria-label="New password"
        data-testid="auth-password"
        placeholder="New password (8+ chars, a number, a lowercase letter)"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <button
        type="submit"
        className="btn btn--primary"
        data-testid="auth-submit"
        disabled={busy}
      >
        {busy ? 'Resetting…' : 'Reset password'}
      </button>
      {shownError && (
        <div className="error-text" data-testid="auth-error">
          {shownError}
        </div>
      )}
    </form>
  );
}
